import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Auth } from '@freelancer/auth';
import {
  BackendUpdateResponse,
  ContestComment,
  ContestQuickviewEntry,
  ContestViewContest,
  CONTEST_ENTRY_AWARD_STATUSES,
  CONTEST_ENTRY_IMAGE_FORMATS,
  CONTEST_ENTRY_VIDEO_FORMATS,
  Datastore,
  DatastoreCollection,
  EntryAction,
  flatten,
  OrderByDirection,
  RequestStatus,
  toNumber,
  User,
} from '@freelancer/datastore';
import { ModalRef } from '@freelancer/ui';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { CalloutPlacement, CalloutSize } from '@freelancer/ui/callout';
import { FileDisplaySize } from '@freelancer/ui/file-display';
import { HeadingSize, HeadingType } from '@freelancer/ui/heading';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { ListItemPadding } from '@freelancer/ui/list-item';
import { Margin } from '@freelancer/ui/margin';
import { PictureDisplay } from '@freelancer/ui/picture';
import {
  RibbonColor,
  RibbonOrientation,
  RibbonPlacement,
} from '@freelancer/ui/ribbon';
import { StickyBehaviour, StickyPosition } from '@freelancer/ui/sticky';
import {
  FontColor,
  FontSize,
  FontStyle,
  FontType,
  FontWeight,
  TextAlign,
  TextTransform,
} from '@freelancer/ui/text';
import { ThumbnailViewerImage } from '@freelancer/ui/thumbnail-viewer';
import { AvatarSize } from '@freelancer/ui/user-avatar';
import {
  ContestCommentContextTypeApi,
  EntryStatusApi,
} from 'api-typings/contests/contests';
import { PerfectScrollbarComponent } from 'ngx-perfect-scrollbar';
import * as Rx from 'rxjs';
import { distinctUntilKeyChanged, flatMap, map, tap } from 'rxjs/operators';
import { PreviewModalResponse } from './preview-modal.types';

@Component({
  selector: 'app-preview-modal',
  template: `
    <ng-container *ngIf="entryFetchStatus$ | async as entryFetchStatus">
      <ng-container
        *ngIf="freelancerFetchStatus$ | async as freelancerFetchStatus"
      >
        <ng-container
          *ngIf="
            (!entryFetchStatus.ready && !entryFetchStatus.error) ||
              (!freelancerFetchStatus.ready && !freelancerFetchStatus.error);
            else loadedState
          "
        >
          <app-preview-modal-loading> </app-preview-modal-loading>
        </ng-container>
      </ng-container>
    </ng-container>

    <ng-template #loadedState>
      <ng-container *ngIf="entry$ | async as entry">
        <ng-container *ngIf="freelancer$ | async as freelancer">
          <fl-bit
            *ngIf="entryIds.length > 1"
            flTrackingSection="ContestViewPage.EntryPreviewModal"
            [flHideMobile]="true"
          >
            <ng-template #navigation let-index="index">
              <fl-link
                class="EntryPreview-navigator EntryPreview-navigator--previous"
                flTrackingLabel="ViewPreviousEntry"
                flTrackingReferenceType="entry_id"
                flTrackingReferenceId="{{ entry.id }}"
                (click)="viewEntry(index - 1)"
              >
                <fl-icon
                  [name]="'ui-chevron-left'"
                  [color]="IconColor.LIGHT"
                  [size]="IconSize.LARGE"
                ></fl-icon>
              </fl-link>

              <fl-link
                class="EntryPreview-navigator EntryPreview-navigator--next"
                flTrackingLabel="ViewNextEntry"
                flTrackingReferenceType="entry_id"
                flTrackingReferenceId="{{ entry.id }}"
                (click)="viewEntry(index + 1)"
              >
                <fl-icon
                  [name]="'ui-chevron-right'"
                  [color]="IconColor.LIGHT"
                  [size]="IconSize.LARGE"
                ></fl-icon>
              </fl-link>
            </ng-template>
            <ng-container
              *ngTemplateOutlet="
                navigation;
                context: { index: currentEntryIndex$ | async }
              "
            ></ng-container>
          </fl-bit>

          <fl-container flTrackingSection="ContestViewPage.EntryPreviewModal">
            <fl-grid class="EntryPreview-layout">
              <fl-col
                class="EntryPreviewMobile-layout"
                [col]="12"
                [flHideTablet]="true"
                [flHideDesktop]="true"
              >
                <fl-bit
                  class="EntryPreviewMobile-header"
                  [flSticky]="true"
                  [flStickyBehaviour]="StickyBehaviour.ALWAYS"
                >
                  <fl-bit class="EntryPreviewMobile-header-title">
                    <fl-heading
                      i18n="Entry number header"
                      [size]="HeadingSize.XXSMALL"
                      [headingType]="HeadingType.H1"
                      [flMarginRight]="Margin.XXSMALL"
                      [flMarginBottom]="
                        (updateResponse$ | async)
                          ? Margin.XXXSMALL
                          : Margin.NONE
                      "
                    >
                      Entry #{{ entry.number }}
                    </fl-heading>
                    <fl-icon
                      *ngIf="isSealed$ | async"
                      [name]="'ui-lock-closed'"
                      [size]="IconSize.SMALL"
                      [color]="IconColor.INHERIT"
                    ></fl-icon>
                  </fl-bit>

                  <ng-container *ngIf="updateResponse$ | async as response">
                    <app-entry-update-error
                      *ngIf="response.errorCode"
                      [response]="response"
                      [source]="entryAction"
                      [entry]="entry"
                    ></app-entry-update-error>
                  </ng-container>

                  <fl-icon
                    class="EntryPreviewMobile-header-close"
                    [name]="'ui-close'"
                    [color]="IconColor.MID"
                    (click)="handleClose()"
                  ></fl-icon>
                </fl-bit>
              </fl-col>

              <fl-col
                class="EntryPreview-layout--primary"
                [col]="12"
                [colTablet]="8"
              >
                <fl-bit class="EntryPreview-header" [flHideMobile]="true">
                  <fl-bit
                    class="EntryPreview-header-content"
                    [flMarginRight]="Margin.XLARGE"
                  >
                    <fl-bit class="EntryPreview-header-content-title">
                      <fl-heading
                        i18n="Entry number header"
                        [size]="HeadingSize.SMALL"
                        [headingType]="HeadingType.H1"
                        [flMarginRight]="Margin.XXSMALL"
                      >
                        Entry #{{ entry.number }}
                      </fl-heading>
                      <fl-icon
                        *ngIf="isSealed$ | async"
                        [name]="'ui-lock-closed'"
                        [color]="IconColor.INHERIT"
                      ></fl-icon>
                    </fl-bit>

                    <fl-text
                      *ngIf="
                        !(isWithdrawn$ | async);
                        else attachmentsTitleWithdrawn
                      "
                      i18n="Entry attachments and position tracker text"
                      [size]="FontSize.XSMALL"
                      [color]="FontColor.MID"
                      [textTransform]="TextTransform.UPPERCASE"
                      [flMarginBottom]="Margin.NONE"
                    >
                      Attachments ({{ currentFileIndex + 1 }} out of
                      {{ entry.files.length }})
                    </fl-text>
                    <ng-template #attachmentsTitleWithdrawn>
                      <fl-text
                        i18n="Entry attachments and position tracker text"
                        [size]="FontSize.XSMALL"
                        [color]="FontColor.MID"
                        [textTransform]="TextTransform.UPPERCASE"
                        [flMarginBottom]="Margin.XXXSMALL"
                      >
                        Attachments
                      </fl-text>
                    </ng-template>
                    <fl-bit
                      class="EntryPreview-header-content-thumbnails-container"
                      *ngIf="isWithdrawn$ | async; else loadedThumbnailsDesktop"
                    >
                      <fl-user-avatar
                        class="EntryPreview-header-content-thumbnails-placeholder"
                        *flRepeat="3"
                        [flMarginRight]="Margin.XXSMALL"
                        [users]="[]"
                        [size]="AvatarSize.SMALL"
                      ></fl-user-avatar>
                    </fl-bit>
                    <ng-template #loadedThumbnailsDesktop>
                      <fl-thumbnail-viewer
                        class="EntryPreview-header-content-thumbnails"
                        [images]="thumbnails$ | async"
                        [currentIndex]="currentFileIndex"
                        [slidesToShow]="THUMBNAIL_LIMIT_DESKTOP"
                        (slideChange)="onSlideChange($event)"
                      ></fl-thumbnail-viewer>
                    </ng-template>
                  </fl-bit>

                  <fl-bit class="EntryPreview-header-cta">
                    <fl-rating
                      flTrackingLabel="RateEntry"
                      [control]="ratingControl"
                      [hideValue]="true"
                      [readOnly]="
                        !isContestOwner ||
                        (isRejected$ | async) ||
                        (isWithdrawn$ | async)
                      "
                    ></fl-rating>

                    <ng-container
                      *ngIf="
                        showErrorsOnHeader() &&
                        (updateResponse$ | async) as response
                      "
                    >
                      <app-entry-update-error
                        *ngIf="response.errorCode"
                        [flMarginBottom]="Margin.XXXSMALL"
                        [response]="response"
                        [source]="entryAction"
                        [entry]="entry"
                      ></app-entry-update-error>
                    </ng-container>

                    <fl-bit
                      *ngIf="isContestOwner && !isOnMultiAward"
                      class="EntryPreview-header-cta-group"
                      [flMarginBottom]="Margin.XXSMALL"
                    >
                      <ng-container *ngIf="isActive$ | async">
                        <fl-button
                          class="EntryPreview-header-cta-button"
                          i18n="Entry reject button"
                          flTrackingLabel="RejectEntry"
                          flTrackingReferenceType="entry_id"
                          flTrackingReferenceId="{{ entry.id }}"
                          [color]="ButtonColor.DEFAULT"
                          [size]="ButtonSize.SMALL"
                          [busy]="
                            rejectResponse$ &&
                            (rejectResponse$ | async) === null
                          "
                          [flMarginRight]="Margin.SMALL"
                          (click)="onRejectEntry(REJECT_ENTRY_CONTENTS)"
                        >
                          Reject
                        </fl-button>
                        <fl-button
                          class="EntryPreview-header-cta-button"
                          i18n="Entry award button"
                          flTrackingLabel="AwardEntry"
                          flTrackingReferenceType="entry_id"
                          flTrackingReferenceId="{{ entry.id }}"
                          [color]="ButtonColor.SUCCESS"
                          [size]="ButtonSize.SMALL"
                          [disabled]="
                            rejectResponse$ &&
                            (rejectResponse$ | async) === null
                          "
                          (click)="onAwardEntry(entry, freelancer)"
                        >
                          Award
                        </fl-button>
                      </ng-container>

                      <fl-button
                        *ngIf="isRejected$ | async"
                        class="EntryPreview-header-cta-button"
                        i18n="Entry reconsider button"
                        flTrackingLabel="ReconsiderEntry"
                        flTrackingReferenceType="entry_id"
                        flTrackingReferenceId="{{ entry.id }}"
                        [color]="ButtonColor.DEFAULT"
                        [size]="ButtonSize.SMALL"
                        [busy]="
                          reconsiderResponse$ &&
                          (reconsiderResponse$ | async) === null
                        "
                        (click)="onReconsiderEntry(RECONSIDER_ENTRY_CONTENTS)"
                      >
                        Reconsider
                      </fl-button>

                      <fl-button
                        *ngIf="isAwardedOrBought$ | async"
                        class="EntryPreview-header-cta-button"
                        i18n="Entry view handover button"
                        flTrackingLabel="GoToHandover"
                        flTrackingReferenceType="entry_id"
                        flTrackingReferenceId="{{ entry.id }}"
                        [color]="ButtonColor.DEFAULT"
                        [size]="ButtonSize.SMALL"
                        [link]="'/contest/handover.php'"
                        [queryParams]="{ entry_id: entry.id }"
                      >
                        View Handover
                      </fl-button>

                      <fl-button
                        *ngIf="isBeingSold$ | async"
                        class="EntryPreview-header-cta-button"
                        i18n="Entry buy button"
                        flTrackingLabel="GoToEntryFullview"
                        flTrackingReferenceType="entry_id"
                        flTrackingReferenceId="{{ entry.id }}"
                        [color]="ButtonColor.DEFAULT"
                        [size]="ButtonSize.SMALL"
                        (click)="onBuyEntry(entry, freelancer)"
                      >
                        Buy
                      </fl-button>
                    </fl-bit>
                  </fl-bit>
                </fl-bit>

                <fl-bit class="EntryPreview-file-container">
                  <fl-bit
                    class="EntryPreview-file-overlay EntryPreview-file-overlay--dark"
                    *ngIf="isRejected$ | async"
                  >
                    <fl-text
                      class="EntryPreview-file-overlay-text"
                      i18n="Entry rejected text"
                      [color]="FontColor.LIGHT"
                      [weight]="FontWeight.BOLD"
                    >
                      Rejected
                    </fl-text>
                  </fl-bit>
                  <fl-bit
                    class="EntryPreview-file-overlay EntryPreview-file-overlay--light"
                    *ngIf="isWithdrawn$ | async"
                  >
                    <fl-text
                      class="EntryPreview-file-overlay-text"
                      i18n="Entry withdrawn text"
                      [weight]="FontWeight.BOLD"
                    >
                      Withdrawn
                    </fl-text>
                    <fl-bit class="EntryPreview-file-overlay-placeholder">
                      <fl-picture
                        alt="Entry file placeholder"
                        i18n-alt="Entry file placeholder image"
                        [src]="'contest-view-page/entry-file-placeholder.svg'"
                        [flMarginBottom]="Margin.SMALL"
                      ></fl-picture>
                      <fl-text
                        i18n="Entry withdrawn text"
                        [weight]="FontWeight.BOLD"
                        [textAlign]="TextAlign.CENTER"
                      >
                        This entry has been withdrawn by the freelancer
                      </fl-text>
                    </fl-bit>
                  </fl-bit>
                  <fl-ribbon
                    class="EntryPreview-file-ribbon"
                    *ngIf="isAwarded$ | async"
                    iconLabel="Awarded"
                    [color]="RibbonColor.SECONDARY"
                    [icon]="'ui-trophy'"
                    [orientation]="RibbonOrientation.VERTICAL"
                    [placement]="RibbonPlacement.LEFT"
                  ></fl-ribbon>
                  <fl-ribbon
                    class="EntryPreview-file-ribbon"
                    *ngIf="isBought$ | async"
                    iconLabel="Bought"
                    [color]="RibbonColor.SECONDARY_DARK"
                    [icon]="'ui-bought-medal'"
                    [orientation]="RibbonOrientation.VERTICAL"
                    [placement]="RibbonPlacement.LEFT"
                  ></fl-ribbon>
                  <fl-ribbon
                    class="EntryPreview-file-ribbon-text"
                    *ngIf="isHighlighted$ | async"
                    iconLabel="Highlighted"
                    i18n="Highlighted text"
                    [color]="RibbonColor.SECONDARY"
                    [placement]="RibbonPlacement.LEFT"
                  >
                    Highlighted
                  </fl-ribbon>
                  <fl-ribbon
                    class="EntryPreview-file-ribbon-text"
                    *ngIf="isBeingSold$ | async"
                    iconLabel="Buy Price"
                    i18n="Entry buy price text"
                    [color]="RibbonColor.QUATERNARY"
                    [placement]="RibbonPlacement.LEFT"
                  >
                    {{
                      entry.sellPrice || contest?.prize
                        | flCurrency: contest?.currency.code:false:false
                    }}
                  </fl-ribbon>
                  <ng-container *ngIf="!(isWithdrawn$ | async)">
                    <fl-bit class="EntryPreview-file-content">
                      <fl-bit
                        class="EntryPreview-file-imageContainer"
                        *ngIf="isImage(entry.files[currentFileIndex]?.name)"
                      >
                        <fl-picture
                          alt="{{ entry.files[currentFileIndex]?.name }}"
                          [src]="entry.files[currentFileIndex]?.thumbnail900Url"
                          [externalSrc]="true"
                          [display]="PictureDisplay.BLOCK"
                          [boundedWidth]="true"
                        ></fl-picture>
                      </fl-bit>
                      <fl-video
                        class="EntryPreview-file-video"
                        *ngIf="isVideo(entry.files[currentFileIndex]?.name)"
                        [src]="entry.files[currentFileIndex]?.fileUrl"
                      ></fl-video>
                    </fl-bit>
                  </ng-container>
                </fl-bit>
              </fl-col>

              <fl-col
                *ngIf="entry.files.length > 1"
                class="EntryPreviewMobile-thumbnails"
                [col]="12"
                [flHideTablet]="true"
                [flHideDesktop]="true"
              >
                <fl-bit
                  class="EntryPreviewMobile-thumbnails-container"
                  *ngIf="isWithdrawn$ | async; else loadedThumbnailsMobile"
                >
                  <fl-user-avatar
                    class="EntryPreviewMobile-thumbnails-placeholder"
                    *flRepeat="3"
                    [flMarginRight]="Margin.XXSMALL"
                    [users]="[]"
                    [size]="AvatarSize.SMALL"
                  ></fl-user-avatar>
                </fl-bit>
                <ng-template #loadedThumbnailsMobile>
                  <fl-text
                    i18n="Entry files position tracker text"
                    [size]="FontSize.XSMALL"
                    [color]="FontColor.MID"
                    [textTransform]="TextTransform.UPPERCASE"
                  >
                    {{ currentFileIndex + 1 }} out of
                    {{ entry.files.length }}
                  </fl-text>

                  <fl-thumbnail-viewer
                    class="EntryPreviewMobile-thumbnails-viewer"
                    [images]="thumbnails$ | async"
                    [currentIndex]="currentFileIndex"
                    [slidesToShow]="THUMBNAIL_LIMIT_MOBILE"
                    (slideChange)="onSlideChange($event)"
                  ></fl-thumbnail-viewer>
                </ng-template>
              </fl-col>

              <fl-col
                class="EntryPreviewMobile-cta"
                [col]="12"
                [flHideTablet]="true"
                [flHideDesktop]="true"
              >
                <fl-rating
                  [control]="ratingControl"
                  [hideValue]="true"
                  [readOnly]="
                    !isContestOwner ||
                    (isRejected$ | async) ||
                    (isWithdrawn$ | async)
                  "
                ></fl-rating>
                <fl-bit class="EntryPreviewMobile-cta-group">
                  <fl-icon
                    *ngIf="
                      !(isEntryOwner$ | async) &&
                        !(isRejected$ | async) &&
                        !(isWithdrawn$ | async);
                      else disableLikeOnMobile
                    "
                    class="EntryPreview-details-feedback-like"
                    [name]="'ui-thumbs-up'"
                    [color]="entry.isLiked ? IconColor.PRIMARY : IconColor.MID"
                    [flMarginRight]="Margin.XXXSMALL"
                    (click)="onLikeEntry(entry, { isLiked: !entry.isLiked })"
                  ></fl-icon>
                  <ng-template #disableLikeOnMobile>
                    <fl-icon
                      [name]="'ui-thumbs-up'"
                      [color]="
                        entry.isLiked ? IconColor.PRIMARY : IconColor.MID
                      "
                      [flMarginRight]="Margin.XXXSMALL"
                    ></fl-icon>
                  </ng-template>
                  <fl-text> {{ entry.likeCount || 0 }} </fl-text>
                  <!-- TODO: Display comment count when implemented -->
                  <ng-container *ngIf="false">
                    <fl-icon
                      [name]="'ui-community'"
                      [color]="IconColor.MID"
                      [flMarginRight]="Margin.XXXSMALL"
                    ></fl-icon>
                    <fl-text> {{ entry.commentCount }} </fl-text>
                  </ng-container>
                </fl-bit>
              </fl-col>

              <fl-col
                class="EntryPreview-layout--secondary"
                [col]="12"
                [colTablet]="4"
              >
                <perfect-scrollbar class="EntryPreview-scrollbar">
                  <fl-bit class="EntryPreview-details">
                    <fl-bit
                      class="EntryPreview-details-user"
                      [ngClass]="{
                        'EntryPreview-details-user--centerAlign': isContestOwner
                      }"
                      [flMarginBottom]="Margin.SMALL"
                    >
                      <fl-user-avatar
                        [users]="[freelancer]"
                        [size]="AvatarSize.LARGE"
                        [flMarginRight]="Margin.XSMALL"
                        [flHideMobile]="true"
                      ></fl-user-avatar>
                      <fl-user-avatar
                        [users]="[freelancer]"
                        [size]="AvatarSize.MID"
                        [isOnline]="
                          freelancer.onlineOfflineStatus?.status === 'online'
                        "
                        [flMarginRight]="Margin.XSMALL"
                        [flHideTablet]="true"
                        [flHideDesktop]="true"
                      ></fl-user-avatar>
                      <fl-bit>
                        <fl-username
                          class="EntryPreview-details-user-username"
                          [displayName]="freelancer.displayName"
                          [link]="freelancer.profileUrl"
                          [newTab]="true"
                          [truncateText]="true"
                          [username]="freelancer.username"
                          [flHideMobile]="true"
                          [flMarginBottom]="Margin.XXSMALL"
                        ></fl-username>
                        <fl-username
                          [displayName]="freelancer.displayName"
                          [link]="freelancer.profileUrl"
                          [compact]="true"
                          [newTab]="true"
                          [truncateText]="true"
                          [username]="freelancer.username"
                          [flHideDesktop]="true"
                          [flHideTablet]="true"
                        ></fl-username>
                        <fl-bit>
                          <fl-button
                            *ngIf="isContestOwner"
                            i18n="Chat button"
                            flTrackingLabel="ChatEntryOwner"
                            flTrackingReferenceType="freelancer_id"
                            flTrackingReferenceId="{{ freelancer.id }}"
                            [color]="ButtonColor.DEFAULT"
                            [size]="ButtonSize.MINI"
                            [flMarginBottom]="Margin.XXXSMALL"
                            [flHideMobile]="true"
                            (click)="handleChatClick(entry)"
                          >
                            <fl-online-indicator
                              [isOnline]="
                                freelancer.onlineOfflineStatus?.status ===
                                'online'
                              "
                              [flMarginRight]="Margin.XXXSMALL"
                            ></fl-online-indicator>
                            Chat
                          </fl-button>
                        </fl-bit>
                      </fl-bit>
                    </fl-bit>

                    <fl-text
                      class="EntryPreview-details-title"
                      [weight]="FontWeight.BOLD"
                      [flMarginBottom]="Margin.XSMALL"
                    >
                      {{ entry.title }}
                    </fl-text>
                    <fl-text
                      class="EntryPreview-details-description"
                      [flMarginBottom]="Margin.SMALL"
                    >
                      <fl-truncated-text
                        text="{{ entry.description }}"
                        [limit]="DESCRIPTION_LIMIT"
                      >
                      </fl-truncated-text>
                    </fl-text>

                    <fl-list
                      *ngIf="entry.stockItems.length > 0"
                      [bodyEdgeToEdge]="true"
                      [bottomBorder]="true"
                      [clickable]="true"
                      [expandable]="true"
                      [outerPadding]="false"
                      [flMarginBottom]="Margin.MID"
                    >
                      <fl-list-item>
                        <fl-list-item-header
                          class="EntryPreview-details-stock-header"
                        >
                          <fl-text
                            i18n="Entry has stock items header"
                            [style]="FontStyle.ITALIC"
                            [fontType]="FontType.SPAN"
                            [flMarginRight]="Margin.XXXSMALL"
                          >
                            This entry contains licensed content
                          </fl-text>
                          <fl-callout
                            class="EntryPreview-details-stock-callout"
                            [hover]="true"
                            [edgeToEdge]="true"
                            [placement]="CalloutPlacement.BOTTOM"
                            [size]="CalloutSize.SMALL"
                          >
                            <fl-callout-trigger>
                              <fl-icon
                                [name]="'ui-help'"
                                [color]="IconColor.MID"
                                [size]="IconSize.SMALL"
                              ></fl-icon>
                            </fl-callout-trigger>

                            <fl-callout-content>
                              <fl-heading
                                class="EntryPreview-details-stock-callout-heading"
                                i18n="Licensed content callout title"
                                [size]="HeadingSize.XXSMALL"
                                [headingType]="HeadingType.H1"
                                [flMarginBottom]="Margin.XXXSMALL"
                              >
                                Licensed content
                              </fl-heading>
                              <fl-text
                                class="EntryPreview-details-stock-callout-text"
                                i18n="Licensed content callout body message"
                              >
                                The freelancer has declared that this entry
                                contains elements that are not their original
                                work.
                              </fl-text>
                            </fl-callout-content>
                          </fl-callout>
                        </fl-list-item-header>

                        <fl-list-item-body>
                          <fl-list
                            [bodyEdgeToEdge]="true"
                            [bottomBorder]="true"
                            [outerPadding]="false"
                            [padding]="ListItemPadding.XXXSMALL"
                          >
                            <fl-list-item
                              *ngFor="let stockItem of entry.stockItems"
                            >
                              <fl-list-item-body>
                                <fl-grid>
                                  <fl-col
                                    class="EntryPreview-details-stock-body"
                                    [col]="7"
                                  >
                                    <fl-link
                                      class="EntryPreview-details-stock-body-name"
                                      flTrackingLabel="ClickStockItemLink"
                                      flTrackingReferenceType="stock_item_id"
                                      flTrackingReferenceId="{{ stockItem.id }}"
                                      [link]="stockItem.url"
                                      [newTab]="true"
                                      [flMarginBottom]="Margin.XXSMALL"
                                    >
                                      {{ stockItem.name }}
                                    </fl-link>
                                  </fl-col>

                                  <fl-col [col]="5" [pull]="'right'">
                                    <fl-text
                                      *ngIf="!stockItem.isFree"
                                      i18n="Requires purchase text"
                                      [color]="FontColor.MID"
                                      [size]="FontSize.SMALL"
                                      [style]="FontStyle.ITALIC"
                                      [textAlign]="TextAlign.RIGHT"
                                    >
                                      Requires purchase
                                    </fl-text>
                                    <fl-text
                                      *ngIf="stockItem.isFree"
                                      i18n="Stock item is free text"
                                      [color]="FontColor.MID"
                                      [size]="FontSize.SMALL"
                                      [style]="FontStyle.ITALIC"
                                      [textAlign]="TextAlign.RIGHT"
                                    >
                                      Free
                                    </fl-text>
                                  </fl-col>
                                </fl-grid>
                              </fl-list-item-body>
                            </fl-list-item>
                          </fl-list>
                        </fl-list-item-body>
                      </fl-list-item>
                    </fl-list>

                    <fl-bit
                      class="EntryPreview-details-feedback"
                      [flMarginBottom]="Margin.XXSMALL"
                      [flHideMobile]="true"
                    >
                      <fl-icon
                        *ngIf="
                          !(isEntryOwner$ | async) && !(isRejected$ | async);
                          else disableLikeOnDesktop
                        "
                        class="EntryPreview-details-feedback-like"
                        [name]="'ui-thumbs-up'"
                        [color]="
                          entry.isLiked ? IconColor.PRIMARY : IconColor.MID
                        "
                        [size]="IconSize.SMALL"
                        [flMarginRight]="Margin.XXXSMALL"
                        (click)="
                          onLikeEntry(entry, { isLiked: !entry.isLiked })
                        "
                      ></fl-icon>
                      <ng-template #disableLikeOnDesktop>
                        <fl-icon
                          [name]="'ui-thumbs-up'"
                          [color]="
                            entry.isLiked ? IconColor.PRIMARY : IconColor.MID
                          "
                          [size]="IconSize.SMALL"
                          [flMarginRight]="Margin.XXXSMALL"
                        ></fl-icon>
                      </ng-template>
                      <fl-text>
                        {{ entry.likeCount || 0 }}
                      </fl-text>
                      <!-- TODO: Display comment count when implemented -->
                      <ng-container *ngIf="false">
                        <fl-icon
                          [name]="'ui-community'"
                          [color]="IconColor.MID"
                          [size]="IconSize.SMALL"
                          [flMarginRight]="Margin.XXXSMALL"
                        ></fl-icon>
                        <fl-text> {{ entry.commentCount }} </fl-text>
                      </ng-container>
                    </fl-bit>

                    <ng-container
                      *ngIf="
                        !showErrorsOnHeader() &&
                        (updateResponse$ | async) as response
                      "
                    >
                      <app-entry-update-error
                        *ngIf="response.errorCode"
                        [flMarginBottom]="Margin.XXXSMALL"
                        [response]="response"
                        [source]="entryAction"
                        [entry]="entry$ | async"
                        [flHideMobile]="true"
                      ></app-entry-update-error>
                    </ng-container>
                  </fl-bit>
                  <fl-hr [flMarginBottom]="Margin.SMALL"></fl-hr>
                  <fl-bit
                    class="EntryPreview-tab"
                    [flMarginBottom]="Margin.SMALL"
                  >
                    <fl-heading
                      i18n="Comments heading"
                      [flMarginBottom]="Margin.SMALL"
                      [headingType]="HeadingType.H1"
                      [size]="HeadingSize.XXSMALL"
                    >
                      Comments
                    </fl-heading>
                  </fl-bit>
                  <fl-bit class="EntryPreview-comments">
                    <app-comment-section
                      [commentLimit]="commentLimit$ | async"
                      [commentsRepliesMap]="commentsRepliesMap"
                      [commentsUsers]="commentsUsers$ | async"
                      [contest]="contest"
                      [entry]="entry"
                      [isQuickView]="true"
                      [parentCommentsCollection]="parentCommentsCollection"
                      [user]="user$ | async"
                      (viewMoreComments)="onViewMoreComments()"
                    ></app-comment-section>
                  </fl-bit>
                </perfect-scrollbar>
              </fl-col>
            </fl-grid>
          </fl-container>

          <fl-container
            *ngIf="isContestOwner && !isOnMultiAward"
            class="EntryPreviewMobile-footer"
            flTrackingSection="ContestViewPage.EntryPreviewModal"
            [flSticky]="true"
            [flStickyBehaviour]="StickyBehaviour.ALWAYS"
            [flStickyPosition]="StickyPosition.BOTTOM"
            [flHideTablet]="true"
            [flHideDesktop]="true"
          >
            <fl-bit class="EntryPreviewMobile-footer-cta">
              <ng-container *ngIf="isActive$ | async">
                <fl-button
                  class="EntryPreviewMobile-footer-cta-button"
                  i18n="Entry reject button"
                  flTrackingLabel="RejectEntry"
                  flTrackingReferenceType="entry_id"
                  flTrackingReferenceId="{{ entry.id }}"
                  [color]="ButtonColor.DEFAULT"
                  [size]="ButtonSize.SMALL"
                  [busy]="rejectResponse$ && (rejectResponse$ | async) === null"
                  [flMarginRight]="Margin.SMALL"
                  (click)="onRejectEntry(REJECT_ENTRY_CONTENTS)"
                >
                  Reject
                </fl-button>
                <fl-button
                  class="EntryPreviewMobile-footer-cta-button"
                  i18n="Entry award button"
                  flTrackingLabel="AwardEntry"
                  flTrackingReferenceType="entry_id"
                  flTrackingReferenceId="{{ entry.id }}"
                  [color]="ButtonColor.SUCCESS"
                  [size]="ButtonSize.SMALL"
                  [disabled]="
                    rejectResponse$ && (rejectResponse$ | async) === null
                  "
                  (click)="onAwardEntry(entry, freelancer)"
                >
                  Award
                </fl-button>
              </ng-container>

              <fl-button
                *ngIf="isRejected$ | async"
                i18n="Entry reconsider button"
                flTrackingLabel="ReconsiderEntry"
                flTrackingReferenceType="entry_id"
                flTrackingReferenceId="{{ entry.id }}"
                [color]="ButtonColor.DEFAULT"
                [size]="ButtonSize.SMALL"
                [display]="'block'"
                [busy]="
                  reconsiderResponse$ && (reconsiderResponse$ | async) === null
                "
                (click)="onReconsiderEntry(RECONSIDER_ENTRY_CONTENTS)"
              >
                Reconsider
              </fl-button>

              <fl-button
                *ngIf="isAwardedOrBought$ | async"
                i18n="Entry view handover button"
                flTrackingLabel="GoToHandover"
                flTrackingReferenceType="entry_id"
                flTrackingReferenceId="{{ entry.id }}"
                [color]="ButtonColor.DEFAULT"
                [size]="ButtonSize.SMALL"
                [display]="'block'"
                [link]="'/contest/handover.php'"
                [queryParams]="{ entry_id: entry.id }"
              >
                View Handover
              </fl-button>

              <fl-button
                *ngIf="isBeingSold$ | async"
                i18n="Entry buy button"
                flTrackingLabel="BuyEntry"
                flTrackingReferenceType="entry_id"
                flTrackingReferenceId="{{ entry.id }}"
                [color]="ButtonColor.DEFAULT"
                [size]="ButtonSize.SMALL"
                [display]="'block'"
                (click)="onBuyEntry(entry, freelancer)"
              >
                Buy
              </fl-button>
            </fl-bit>
          </fl-container>
        </ng-container>
      </ng-container>
    </ng-template>
  `,
  styleUrls: ['./preview-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreviewModalComponent implements OnInit, OnDestroy {
  AvatarSize = AvatarSize;
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  CalloutPlacement = CalloutPlacement;
  CalloutSize = CalloutSize;
  FileDisplaySize = FileDisplaySize;
  FontColor = FontColor;
  FontSize = FontSize;
  FontStyle = FontStyle;
  FontType = FontType;
  FontWeight = FontWeight;
  HeadingSize = HeadingSize;
  HeadingType = HeadingType;
  IconColor = IconColor;
  IconSize = IconSize;
  ListItemPadding = ListItemPadding;
  Margin = Margin;
  PictureDisplay = PictureDisplay;
  RibbonColor = RibbonColor;
  RibbonOrientation = RibbonOrientation;
  RibbonPlacement = RibbonPlacement;
  StickyBehaviour = StickyBehaviour;
  StickyPosition = StickyPosition;
  TextAlign = TextAlign;
  TextTransform = TextTransform;

  @Input() contest: ContestViewContest;
  @Input() imageSliderIndex: number;
  @Input() isContestOwner: boolean;
  @Input() hasContestAwardedEntry: boolean;
  @Input() initialEntryId: number;
  @Input() entryIds: ReadonlyArray<number>;
  @Input() isOnMultiAward: boolean;
  @Input() canMultiAward: boolean;

  readonly THUMBNAIL_LIMIT_DESKTOP = 5;
  readonly THUMBNAIL_LIMIT_MOBILE = 7;
  readonly REJECT_ENTRY_CONTENTS = { status: EntryStatusApi.ELIMINATED };
  readonly RECONSIDER_ENTRY_CONTENTS = { status: EntryStatusApi.ACTIVE };
  readonly DESCRIPTION_LIMIT = 200;

  currentEntryIndexSubject$: Rx.BehaviorSubject<number>;
  currentEntryIndex$: Rx.Observable<number>;
  entryIdSubject$: Rx.BehaviorSubject<number>;
  entryId$: Rx.Observable<number>;
  entry$: Rx.Observable<ContestQuickviewEntry>;
  freelancer$: Rx.Observable<User>;
  user$: Rx.Observable<User>;
  isHighlighted$: Rx.Observable<boolean>;
  isActive$: Rx.Observable<boolean>;
  isRejected$: Rx.Observable<boolean>;
  isWithdrawn$: Rx.Observable<boolean>;
  isSealed$: Rx.Observable<boolean>;
  isAwarded$: Rx.Observable<boolean>;
  isBought$: Rx.Observable<boolean>;
  isAwardedOrBought$: Rx.Observable<boolean>;
  isBeingSold$: Rx.Observable<boolean>;
  isEntryOwner$: Rx.Observable<boolean>;
  thumbnails$: Rx.Observable<ReadonlyArray<ThumbnailViewerImage>>;

  entryFetchStatus$: Rx.Observable<RequestStatus<'contestQuickviewEntries'>>;
  freelancerFetchStatus$: Rx.Observable<RequestStatus<'users'>>;

  ratingControl = new FormControl();
  ratingSubscription: Rx.Subscription;
  entrySubscription: Rx.Subscription;
  currentFileIndex: number;

  entryAction: EntryAction;
  updateResponse$:
    | Promise<BackendUpdateResponse<'contestQuickviewEntries'>>
    | undefined;
  rejectResponse$: Promise<BackendUpdateResponse<'contestQuickviewEntries'>>;
  reconsiderResponse$: Promise<
    BackendUpdateResponse<'contestQuickviewEntries'>
  >;

  commentsRepliesMap = new Map<
    number,
    Rx.Observable<ReadonlyArray<ContestComment>>
  >();
  commentsUsers$: Rx.Observable<Map<number, User>>;
  parentCommentsCollection: DatastoreCollection<'contestComments'>;
  repliesUserIds$: Rx.Observable<ReadonlyArray<number>>;
  private repliesSubscription: Rx.Subscription;

  readonly COMMENT_LIMIT_INCREMENT = 50;
  private commentLimitSubject$ = new Rx.BehaviorSubject<number>(
    this.COMMENT_LIMIT_INCREMENT,
  );
  commentLimit$ = this.commentLimitSubject$.asObservable();

  @ViewChild(PerfectScrollbarComponent, { static: false })
  psbComponent: PerfectScrollbarComponent;

  /**
   * Placeholder variables.
   * TODO: Replace variables with dynamic data -- hooked from the backend.
   */
  country = 'au';

  constructor(
    private auth: Auth,
    private datastore: Datastore,
    private location: Location,
    private modalRef: ModalRef<PreviewModalComponent>,
  ) {}

  ngOnInit() {
    this.entryIdSubject$ = new Rx.BehaviorSubject<number>(this.initialEntryId);
    this.entryId$ = this.entryIdSubject$.asObservable();

    const initialEntryIndex = this.entryIds.findIndex(
      id => id === this.initialEntryId,
    );
    this.currentEntryIndexSubject$ = new Rx.BehaviorSubject<number>(
      initialEntryIndex,
    );
    this.currentEntryIndex$ = this.currentEntryIndexSubject$.asObservable();

    // Datastore call needed to have the entry status change reflected on the modal upon user CTA
    const entriesDocument = this.datastore.document(
      'contestQuickviewEntries',
      this.entryId$,
    );
    this.entry$ = entriesDocument.valueChanges();
    this.entryFetchStatus$ = entriesDocument.status$;

    this.entrySubscription = this.entry$.subscribe(entry => {
      this.location.replaceState(entry.seoUrl);
    });

    const freelancerDocument = this.datastore.document(
      'users',
      this.entry$.pipe(map(entry => entry.ownerId)),
    );
    this.freelancer$ = freelancerDocument.valueChanges();
    this.freelancerFetchStatus$ = freelancerDocument.status$;

    this.isHighlighted$ = this.entry$.pipe(
      map(
        entry =>
          !!(
            entry.upgrades &&
            entry.upgrades.highlight &&
            !this.hasContestAwardedEntry &&
            ![
              EntryStatusApi.ELIMINATED,
              EntryStatusApi.WITHDRAWN,
              EntryStatusApi.WITHDRAWN_ELIMINATED,
            ].includes(entry.status)
          ),
      ),
    );

    this.isActive$ = this.entry$.pipe(
      map(
        entry =>
          !this.hasContestAwardedEntry &&
          entry.status === EntryStatusApi.ACTIVE,
      ),
    );

    this.isRejected$ = this.entry$.pipe(
      map(entry =>
        [
          EntryStatusApi.ELIMINATED,
          EntryStatusApi.WITHDRAWN_ELIMINATED,
        ].includes(entry.status),
      ),
    );

    this.isWithdrawn$ = this.entry$.pipe(
      map(entry =>
        [
          EntryStatusApi.WITHDRAWN,
          EntryStatusApi.WITHDRAWN_ELIMINATED,
        ].includes(entry.status),
      ),
    );

    this.isSealed$ = this.entry$.pipe(
      map(entry => !!(entry.upgrades && entry.upgrades.sealed)),
    );

    this.isAwarded$ = this.entry$.pipe(
      map(entry => CONTEST_ENTRY_AWARD_STATUSES.includes(entry.status)),
    );

    this.isBought$ = this.entry$.pipe(
      map(entry => entry.status === EntryStatusApi.BOUGHT),
    );

    this.isAwardedOrBought$ = Rx.combineLatest([
      this.isAwarded$,
      this.isBought$,
    ]).pipe(map(([isAwarded, isBought]) => isAwarded || isBought));

    this.isBeingSold$ = this.entry$.pipe(
      map(
        entry =>
          !!(
            this.hasContestAwardedEntry &&
            entry.status === EntryStatusApi.ACTIVE &&
            (entry.sellPrice || this.contest.prize)
          ),
      ),
    );

    const entryEmissionOnlyWhenIdChanges$ = this.entry$.pipe(
      distinctUntilKeyChanged('id'),
      tap(entry => {
        // unsubscribe if there's an existing subscription
        // to prevent calling rate entry function on entry change
        // when setting initial rating
        if (this.ratingSubscription) {
          this.ratingSubscription.unsubscribe();
        }

        this.ratingControl.setValue(entry.rating);
        this.ratingSubscription = this.ratingControl.valueChanges.subscribe(
          rating => this.onRateEntry({ rating }),
        );
      }),
    );

    this.thumbnails$ = entryEmissionOnlyWhenIdChanges$.pipe(
      map(entry =>
        entry.files.map(file => ({
          src: file.thumbnail80Url || '',
          alt: ' ',
        })),
      ),
    );

    const userId$ = this.auth.getUserId().pipe(map(id => toNumber(id)));
    this.user$ = this.datastore.document('users', userId$).valueChanges();

    this.isEntryOwner$ = Rx.combineLatest([userId$, this.entry$]).pipe(
      map(([userId, entry]) => userId === entry.ownerId),
    );

    this.currentFileIndex = this.imageSliderIndex;

    this.parentCommentsCollection = this.datastore.collection(
      'contestComments',
      query =>
        query
          .where('contextId', '==', this.entryId$)
          .where('contextType', '==', ContestCommentContextTypeApi.ENTRY)
          .where('parentId', '==', undefined)
          .orderBy('dateLastComment', OrderByDirection.DESC)
          .limit(this.commentLimit$),
    );

    const replyQueriesSubject$ = new Rx.BehaviorSubject<
      Rx.Observable<ReadonlyArray<ContestComment>>[]
    >([]);
    const replyQueries$ = replyQueriesSubject$.asObservable();

    // Get replies per parent comment
    this.repliesSubscription = this.parentCommentsCollection
      .valueChanges()
      .subscribe(parentComments => {
        parentComments.forEach(parentComment => {
          if (!this.commentsRepliesMap.has(parentComment.id)) {
            // We want to display the latest replies based on a limit in ascending order.
            // To get the latest replies without having to provide an offset, the backend
            // queries for replies in descending order with the limit. We then reverse the
            // list to be displayed in ascending order.
            const queryResult$ = this.datastore
              .collection('contestComments', query =>
                query
                  .where('parentId', '==', parentComment.id)
                  .orderBy('dateLastComment', OrderByDirection.DESC),
              )
              .valueChanges()
              .pipe(map(comments => comments.slice().reverse()));

            this.commentsRepliesMap.set(parentComment.id, queryResult$);

            replyQueriesSubject$.next([
              ...replyQueriesSubject$.value,
              queryResult$,
            ]);
          }
        });
      });

    // Get replies user IDs
    this.repliesUserIds$ = replyQueries$.pipe(
      flatMap(replyQueries =>
        Rx.combineLatest(replyQueries).pipe(
          map(flatten),
          map(allReplies => allReplies.map(reply => reply.fromUserId)),
        ),
      ),
    );

    // Get parent comments user IDs
    const parentCommentsUserIds$ = this.parentCommentsCollection
      .valueChanges()
      .pipe(
        map(parentComments =>
          parentComments.map(parentComment => parentComment.fromUserId),
        ),
      );

    // Get all comments users
    this.commentsUsers$ = this.datastore
      .collection('users', query =>
        Rx.combineLatest([this.repliesUserIds$, parentCommentsUserIds$]).pipe(
          map(([repliesUserIds, parentCommentsUserIds]) =>
            query.where('id', 'in', [
              ...repliesUserIds,
              ...parentCommentsUserIds,
            ]),
          ),
        ),
      )
      .valueChanges()
      .pipe(map(users => new Map(users.map(user => [user.id, user]))));
  }

  onAwardEntry(entry: ContestQuickviewEntry, freelancer: User) {
    // TODO: Open award modal directly in preview modal after T82677 is done
    this.modalRef.close({
      response: PreviewModalResponse.AWARD,
      entry,
      freelancer,
      canMultiAward: this.canMultiAward,
    });
  }

  onBuyEntry(entry: ContestQuickviewEntry, freelancer: User) {
    this.modalRef.close({
      response: PreviewModalResponse.BUY,
      contest: this.contest,
      entry,
      freelancer,
    });
  }

  onSlideChange(index: number) {
    this.currentFileIndex = index;
  }

  onViewMoreComments() {
    this.commentLimitSubject$.next(
      this.commentLimitSubject$.getValue() + this.COMMENT_LIMIT_INCREMENT,
    );
  }

  isImage(filename: string) {
    let fileType = filename.split('.').pop();
    fileType = fileType ? fileType.toLowerCase() : filename;
    return CONTEST_ENTRY_IMAGE_FORMATS.includes(fileType);
  }

  isVideo(filename: string) {
    let fileType = filename.split('.').pop();
    fileType = fileType ? fileType.toLowerCase() : filename;
    return CONTEST_ENTRY_VIDEO_FORMATS.includes(fileType);
  }

  showErrorsOnHeader() {
    const headerEntryActions = [
      EntryAction.RATE,
      EntryAction.REJECT,
      EntryAction.RECONSIDER,
    ];
    return headerEntryActions.includes(this.entryAction);
  }

  onRateEntry(changes: Partial<ContestQuickviewEntry>) {
    this.entryAction = EntryAction.RATE;
    this.updateResponse$ = this.updateEntry(changes);
  }

  onRejectEntry(changes: Partial<ContestQuickviewEntry>) {
    this.entryAction = EntryAction.REJECT;
    this.rejectResponse$ = this.updateEntry(changes);
    this.updateResponse$ = this.rejectResponse$;
  }

  onReconsiderEntry(changes: Partial<ContestQuickviewEntry>) {
    this.entryAction = EntryAction.RECONSIDER;
    this.reconsiderResponse$ = this.updateEntry(changes);
    this.updateResponse$ = this.reconsiderResponse$;
  }

  onLikeEntry(
    entry: ContestQuickviewEntry,
    changes: Partial<ContestQuickviewEntry>,
  ) {
    this.entryAction = entry.isLiked ? EntryAction.DISLIKE : EntryAction.LIKE;
    this.updateResponse$ = this.updateEntry(changes);
  }

  updateEntry(changes: Partial<ContestQuickviewEntry>) {
    return this.datastore
      .document('contestQuickviewEntries', this.entryId$)
      .update(changes);
  }

  viewEntry(index: number) {
    this.currentFileIndex = 0;

    // Set index when hitting the end of the list. It should behave like a
    // circular list.
    let nextEntryIndex = index;

    if (index < 0) {
      nextEntryIndex = this.entryIds.length - 1;
    } else if (index === this.entryIds.length) {
      nextEntryIndex = 0;
    }

    // unset error message
    this.updateResponse$ = undefined;
    this.currentEntryIndexSubject$.next(nextEntryIndex);
    this.entryIdSubject$.next(this.entryIds[nextEntryIndex]);
  }

  handleChatClick(entry: ContestQuickviewEntry) {
    this.modalRef.close({ response: PreviewModalResponse.START_CHAT, entry });
  }

  handleClose() {
    this.modalRef.close();
  }

  ngOnDestroy() {
    if (this.ratingSubscription) {
      this.ratingSubscription.unsubscribe();
    }

    if (this.entrySubscription) {
      this.entrySubscription.unsubscribe();
    }

    if (this.repliesSubscription) {
      this.repliesSubscription.unsubscribe();
    }
  }
}
